import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { getRandomColorWithOpacity } from '@piqet-angular/data';
import {
  parseNumericValue,
  PEF_NORMALIZED,
  PEF_WEIGHTED,
  superAdminRoleStr,
  TableColumn,
  TableRow,
  TableConfig,
  DecimalFormatPipePipe,
  ImpactDataFormateEnum,
} from '@piqet-angular/shared';
import * as Highcharts from 'highcharts';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { CookieService } from 'ngx-cookie-service';

interface IndicatorOption {
  name: string;
  value: string;
  index: number;
}

interface ValueRange {
  min: number;
  max: number;
  label: string;
  isMin5?: boolean;
  isMax5?: boolean;
}

interface TableDataItem {
  name: string;
  values: Array<{
    value: number | null;
    percentage?: string;
    inRange: boolean;
    originalImpactValue?: string;
    originalPercentage?: string;
    originalValueString?: string;
    [key: string]: any;
  }>;
}

@Component({
  selector: 'piqet-angular-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnChanges {
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  private decimalFormat = new DecimalFormatPipePipe();
  isFullscreen: boolean = false;
  viewMode: 'chart' | 'table' = 'chart'; // New property for view mode

  constructor(
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private sharedService: SharedService
  ) {}

  get isSuperAdmin(): boolean {
    try {
      const userRole = this.cookieService.get('u_type');
      const decryptUserRole = this.sharedService.decodeURIComponentParam(userRole)?.toLowerCase();
      if (superAdminRoleStr === decryptUserRole) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking super admin status:', error);
      return false;
    }
  }

  @Input() chartData: any[] = [];
  @Input() chartOpacity: number = 0.5;
  @Input() title: string = '';
  @Input() indicatorsName: string = '';
  @Input() isChartNormalized: boolean = false;
  @Input() isShowValue: boolean = false;
  @Input() chartType: string = 'column';
  @Input() stackingMode: string = '';
  @Input() chartHeight: number = 500;
  @Input() selectedPEFType: string = 'Weighted';
  /** Resolved palette hex array; first wins as series color when none is set on the data point. */
  @Input() paletteColors: string[] | null = null;
  /** Subset of ReportStyleConfig needed to drive Highcharts options. Untyped to avoid circular dep with features lib. */
  @Input() styleConfig: any = null;
  /**
   * Per-call override for the per-chart-type label config (rotation, wrap).
   * Untyped to avoid circular import with the features lib; expected shape:
   * `{ labelRotation: number | 'auto' | number[]; labelWrap: boolean }`.
   * When omitted, falls back to `DEFAULT_CHART_LABEL_CONFIG[chartType]`.
   */
  @Input() labelConfigOverride: any = null;
  readonly WEIGHTED_TEXT = 'Weighted';

  @Output() changePEFCategoriesEmit = new EventEmitter<string>();

  isLoading: boolean = false;
  /** Final SVG height after the long-label / legend budget is applied. */
  computedChartHeight: number = 500;
  readonly PEFWeightTitle = PEF_WEIGHTED;
  readonly PEFNormalizeTitle = PEF_NORMALIZED;

  // For value range filtering
  valueRanges: ValueRange[] = [];
  selectedRangeIndex: number = 0;

  allIndicators: string[] = [];
  filteredIndicators: string[] = [];

  // For indicator multi-select
  selectableIndicators: IndicatorOption[] = [];
  selectedIndicators: IndicatorOption[] = [];

  // Descriptive range names
  readonly RANGE_NAMES = [
    'All values',
    'Very Low',
    'Low',
    'Moderate',
    'High',
    'Very High',
    'Extremely High',
  ];

  isAccordionOpen = false;
  selectableLegendItems: IndicatorOption[] = [];
  selectedLegendItems: IndicatorOption[] = [];
  isLegendAccordionOpen = false;

  ngOnInit() {
    this.initChart();
  }

  changeViewMode(mode: 'chart' | 'table'): void {
    this.viewMode = mode;
    if (mode === 'chart') {
      // Reinitialize chart when switching back to chart view
      this.initChart();
    }
  }

  changePEFCategories(type: string) {
    this.selectedRangeIndex = 0;
    this.changePEFCategoriesEmit.emit(type);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && changes['chartData'].currentValue) {
      this.isLoading = false;
      this.chartData = changes['chartData'].currentValue;
    }
    // Handle both object format { value: 0.4 } and plain number 0.4
    if (changes['chartOpacity']) {
      const opacityInput = changes['chartOpacity'].currentValue;
      this.chartOpacity = typeof opacityInput === 'object' ? opacityInput?.value : opacityInput;
    }
    if (changes['title']) {
      this.title = changes['title'].currentValue?.value ?? changes['title'].currentValue;
    }
    if (changes['indicatorsName']) {
      this.indicatorsName = changes['indicatorsName'].currentValue || '';
    }
    if (changes['isChartNormalized']) {
      this.isChartNormalized = changes['isChartNormalized'].currentValue || false;
    }
    if (changes['isShowValue']) {
      this.isShowValue = changes['isShowValue'].currentValue || false;
    }
    if (changes['chartType']) {
      this.chartType = changes['chartType'].currentValue || 'column';
    }
    if (changes['stackingMode']) {
      this.stackingMode = changes['stackingMode'].currentValue || '';
    }

    if (this.chartData && this.chartData.length > 0) {
      this.prepareChartData();
      this.initChart();
    }
  }

  createValueRanges(sortedValues: number[]): void {
    this.valueRanges = [];

    if (sortedValues.length === 0) return;

    // Get min and max values, ignoring zeros if present
    const nonZeroValues = sortedValues.filter(v => v !== 0);
    const minValue = nonZeroValues.length ? Math.min(...nonZeroValues) : 0;
    const maxValue = Math.max(...sortedValues);

    // Add "All Values" as first option
    this.valueRanges.push({
      min: Number.NEGATIVE_INFINITY,
      max: Number.POSITIVE_INFINITY,
      label: this.RANGE_NAMES[0],
    });

    if (sortedValues.length > 0) {
      this.createDescriptiveRanges(sortedValues, minValue, maxValue);
    }
  }

  private createDescriptiveRanges(
    sortedValues: number[],
    minValue: number,
    maxValue: number
  ): void {
    // Create 5 descriptive ranges
    const nonZeroValues = sortedValues.filter(v => v !== 0);

    // If there are very few unique values, handle specially
    const uniqueValues = [...new Set(nonZeroValues)];

    if (uniqueValues.length <= 6) {
      // Handle small number of unique values
      for (let i = 0; i < Math.min(uniqueValues.length, 5); i++) {
        this.valueRanges.push({
          min: i < uniqueValues.length ? uniqueValues[i] : 0,
          max: i + 1 < uniqueValues.length ? uniqueValues[i + 1] : Number.POSITIVE_INFINITY,
          label: this.RANGE_NAMES[i + 1],
        });
      }
      return;
    }

    // Calculate percentiles for the 5 ranges
    const percentiles = [0, 0.2, 0.4, 0.6, 0.8, 1.0];
    const rangeThresholds = percentiles.map(p => {
      const index = Math.floor(p * nonZeroValues.length);
      return nonZeroValues[Math.min(index, nonZeroValues.length - 1)];
    });

    // Create the 5 descriptive ranges
    for (let i = 0; i < 5; i++) {
      this.valueRanges.push({
        min: rangeThresholds[i],
        max: rangeThresholds[i + 1],
        label: this.RANGE_NAMES[i + 1],
      });
    }
  }

  prepareChartData(): void {
    // Extract all indicators
    const categories = this.isChartNormalized
      ? this.chartData[0].indicators
      : this.chartData[0].hasOwnProperty('itemName')
      ? this.chartData[0].itemName
      : this.chartData[0].componentName;
    this.allIndicators = categories
      ? categories.split(';')
      : this.chartData.length > 0 && this.chartData[0].data
      ? ['Value 1', 'Value 2']
      : [];

    this.filteredIndicators = [...this.allIndicators];

    this.selectableIndicators = this.allIndicators.map((indicator, index) => ({
      name: indicator,
      value: indicator,
      index: index,
    }));

    this.selectedIndicators = [...this.selectableIndicators]; // Select all indicators by default
    this.filteredIndicators = [...this.allIndicators];

    const allValues: number[] = [];

    if (this.chartData[0].impactValues) {
      // Use the original data format
      this.chartData.forEach(item => {
        const values = item.impactValues.split(';').map((val: string) => parseNumericValue(val));
        allValues.push(...values);
      });
    }

    const validValues = allValues.filter(val => !isNaN(val) && isFinite(val));

    const sortedValues = [...validValues].sort((a, b) => a - b);
    this.createValueRanges(sortedValues);
    this.selectableLegendItems = this.chartData
      .filter(item => item.name !== 'Total')
      .map((item, index) => ({
        name: item.name,
        value: item.name,
        index: index,
      }));

    this.selectedLegendItems = [...this.selectableLegendItems];
  }

  onIndicatorSelectionChange(): void {
    this.filteredIndicators = this.selectedIndicators.map(i => i.name);
    this.initChart();
  }

  // Helper method to check if a value is within the selected range
  isValueInSelectedRange(value: number): boolean {
    if (this.selectedRangeIndex === 0) {
      // "All values" is selected
      return true;
    }

    const selectedRange = this.valueRanges[this.selectedRangeIndex];
    return value >= selectedRange.min && value <= selectedRange.max;
  }

  // Filter data based on selected range
  filterDataByRange(data: number[]) {
    if (this.selectedRangeIndex === 0) {
      return data;
    }

    return data.map(value => (this.isValueInSelectedRange(value) ? value : null));
  }

  // Get scenario names for table headers (works for both weighted and normalized)
  getScenarioNames(): string[] {
    if (!this.chartData || this.chartData.length === 0) return [];
    // For weighted view, get the scenario names from itemName field
    if (!this.isChartNormalized) {
      const firstItem = this.chartData[0]?.itemName || this.chartData[0]?.componentName;
      if (firstItem) {
        return firstItem.split(';');
      }
    }

    // For normalized view, return the scenario names (TUBE, TUBE_TP)
    if (this.isChartNormalized) {
      return this.chartData
        .filter(item => item.name && item.name !== 'Total')
        .map(item => item.name);
    }

    return [];
  }

  // Get data formatted for table (vertical layout for both weighted and normalized)
  getTableData(): TableDataItem[] {
    if (!this.chartData || this.chartData.length === 0) return [];

    const tableData: TableDataItem[] = [];

    if (this.isChartNormalized) {
      // For normalized data: categories as rows, scenarios as columns
      // Always show all indicators in table view, regardless of fullscreen mode
      const indicatorsToShow = this.allIndicators;

      // Create a row for each indicator/category
      indicatorsToShow.forEach(indicatorName => {
        const indicatorIndex = this.allIndicators.indexOf(indicatorName);
        if (indicatorIndex === -1) return;

        const rowData: TableDataItem = {
          name: indicatorName,
          values: [],
        };

        // Add values from each scenario
        this.chartData.forEach(scenario => {
          if (scenario.name !== 'Total') {
            const allValuesStrings = scenario.impactValues.split(';');
            const allValuesNumeric = allValuesStrings.map((v: string) => parseFloat(v));
            const allPercentages = scenario.percentage ? scenario.percentage.split(';') : [];

            const numericValue = allValuesNumeric[indicatorIndex];
            const originalValueString = allValuesStrings[indicatorIndex];
            const inRange = this.isValueInSelectedRange(numericValue);

            rowData.values.push({
              value: inRange ? numericValue : null,
              percentage: allPercentages[indicatorIndex],
              inRange: inRange,
              originalImpactValue: originalValueString ?? '-',
              originalPercentage: allPercentages[indicatorIndex] ?? '-',
              originalValueString: originalValueString,
            });
          }
        });

        tableData.push(rowData);
      });
    } else {
      // For weighted data: categories as rows, scenarios as columns
      // Always show all categories in table view
      this.chartData.forEach(item => {
        if (item.name !== 'Total') {
          const rowData: TableDataItem = {
            name: item.name,
            values: [],
          };

          if (item.impactValues) {
            const allValuesStrings = item.impactValues.split(';'); // Keep as strings
            const allValuesNumeric = allValuesStrings.map((value: string) => parseFloat(value)); // Parse for calculations
            const allPercentages = item.percentage ? item.percentage.split(';') : [];

            // For each scenario
            allValuesNumeric.forEach((numericValue: any, index: number) => {
              const originalValueString = allValuesStrings[index];
              const inRange = this.isValueInSelectedRange(numericValue);

              rowData.values.push({
                value: inRange ? numericValue : null,
                percentage: allPercentages[index],
                inRange: inRange,
                originalImpactValue: originalValueString ?? '-',
                originalPercentage: allPercentages[index] ?? '-',
                originalValueString: originalValueString,
              });
            });
          }

          tableData.push(rowData);
        }
      });
    }

    return tableData;
  }
  getWeightedTableData(): TableDataItem[] {
    return this.getTableData();
  }

  getNormalizedTableData(): TableDataItem[] {
    return this.getTableData();
  }

  // Format value for table display (shared 3-SF / 3-decimal / scientific rules)
  formatTableValue(value: number): string {
    return this.decimalFormat.transform(value, ImpactDataFormateEnum.Default);
  }

  // Get table columns for the common table component
  getTableColumns(): TableColumn[] {
    const scenarioNames = this.getScenarioNames();
    return [
      { key: 'category', label: 'Category', sticky: true },
      ...scenarioNames.map(name => ({
        key: name.toLowerCase().replace(/\s+/g, '_'),
        label: name,
      })),
    ];
  }

  // Transform data for the new dynamic table format
  getTransformedTableData(): TableRow[] {
    const tableData = this.getTableData();
    const scenarioNames = this.getScenarioNames();

    return tableData.map(item => {
      const row: TableRow = {
        category: item.name,
      };

      item.values.forEach((value, index) => {
        const columnKey = scenarioNames[index]?.toLowerCase().replace(/\s+/g, '_');
        if (columnKey) {
          let percentageDisplay: string | number | undefined;
          if (value.percentage !== undefined) {
            const cleaned = value.percentage.replace(/,/g, '');
            percentageDisplay = parseFloat(cleaned);
          }
          if (typeof percentageDisplay === 'number') {
            if (percentageDisplay > 0 && percentageDisplay < 0.1) {
              percentageDisplay = '> 0';
            }
          }

          let displayValue: string;
          if (value.value !== null) {
            if (value.originalValueString) {
              displayValue = value.originalValueString;
            } else {
              // Fallback to formatting the numeric value
              displayValue = this.formatTableValue(value.value);
            }

            if (this.shouldShowPercentage() && value.percentage) {
              displayValue += ` (${percentageDisplay}%)`;
            }
          } else {
            displayValue = '-';
          }

          row[columnKey] = {
            value: value.value,
            displayValue: displayValue,
            cssClass: !value.inRange ? 'warning' : '',
            metadata: {
              percentage: value.percentage,
              inRange: value.inRange,
              originalImpactValue: value.originalImpactValue ?? '-',
              originalPercentage: value.originalPercentage ?? '-',
            },
          };
        }
      });

      return row;
    });
  }

  // Get table configuration
  getTableConfig(): TableConfig {
    return {
      height: '87vh',
      striped: true,
      hover: true,
      stickyHeader: true,
    };
  }

  // Check if percentage should be shown in table
  shouldShowPercentage(): boolean {
    return this.title === this.PEFWeightTitle || this.title === this.PEFNormalizeTitle;
  }

  private resolveStacking(): string | undefined {
    // Explicit 'none' = grouped (no stacking)
    if (this.stackingMode === 'none') return undefined;
    // Explicit stacking mode ('normal', 'percent')
    if (this.stackingMode === 'normal' || this.stackingMode === 'percent') return this.stackingMode;
    // Normalized charts default to grouped (no stacking)
    if (this.isChartNormalized) return undefined;
    // Default: stacked normal
    return 'normal';
  }

  private isGroupedMode(): boolean {
    return this.resolveStacking() === undefined;
  }

  initChart() {
    if (!this.chartData || this.chartData.length === 0) return;

    // Force highcharts-chart component recreation so stacking applies correctly.
    // chart.update() does not handle plotOptions.stacking changes reliably.
    this.isLoading = false;
    this.cdr.detectChanges();

    this.chartOptions = {};

    const indicatorsToShow = this.isFullscreen ? this.filteredIndicators : this.allIndicators;

    const indicatorIndices: number[] = [];
    const usedIndices = new Set<number>();

    indicatorsToShow.forEach(indicator => {
      let index = -1;

      do {
        index = this.allIndicators.indexOf(indicator, index + 1);
      } while (usedIndices.has(index) && index !== -1);

      if (index !== -1) {
        indicatorIndices.push(index);
        usedIndices.add(index);
      }
    });

    // Prepare series data
    const seriesData: any[] = [];

    const allColors = getRandomColorWithOpacity(this.chartOpacity);
    let usedColorIndex = 0;
    const categoryColors: any = {};
    const legendColors: { [key: string]: string } = {};

    this.chartData.forEach(item => {
      if (item.name !== 'Total') {
        if (!categoryColors[item.name]) {
          // Use the item's custom color if provided, otherwise use random color
          if (item.color || item.borderColor) {
            // Extract RGB from rgba/rgb string or use the borderColor
            const colorToUse = item.borderColor || item.color;
            categoryColors[item.name] = {
              rgba: item.color || colorToUse,
              rgb: item.borderColor || colorToUse,
            };
            legendColors[item.name] = item.borderColor || colorToUse;
          } else {
            categoryColors[item.name] = allColors[usedColorIndex % allColors.length];
            legendColors[item.name] = allColors[usedColorIndex % allColors.length].rgb;
            usedColorIndex++;
          }
        }

        let filteredValues: any = [];
        let percentageValues: string[] = [];
        if (item.impactValues) {
          const allValues = item.impactValues
            .split(';')
            .map((value: string) => parseNumericValue(value));
          filteredValues = indicatorIndices.map(index => allValues[index]);
        }

        filteredValues = this.filterDataByRange(filteredValues);

        if (item.percentage) {
          percentageValues = item.percentage.split(';');
        }

        // Add legend click event to each series
        const seriesEntry: any = {
          name: item.name,
          data: filteredValues,
          type: this.chartType,
          color: item.color || categoryColors[item.name].rgba,
          borderColor: item.borderColor || categoryColors[item.name].rgb,
          className: 'top-border-bars',
          // Hidden stages render greyed in the legend but are excluded from the
          // bars and from the y-axis range computation below.
          visible: this.isLegendSeriesVisible(item.name),
          events: {
            // Toggle this stage and rebuild the chart so the y-axis rescales to
            // the remaining visible stages (default Highcharts behaviour only
            // drops the segment and leaves the axis — and the empty space —
            // unchanged).
            legendItemClick: (event: any) => {
              this.toggleLegendSeries(event.target.name);
              // We rebuild the chart ourselves; suppress the default toggle.
              return false;
            },
          },
          custom: {
            percentages: percentageValues,
            isShowPercentage:
              this.title == this.PEFWeightTitle ||
              this.title == this.PEFNormalizeTitle ||
              (item.isShowPercentage === true && percentageValues.length > 0),
            isShowValue: this.isShowValue,
          } as { [key: string]: any },
        };
        // Only add stack grouping when stacking is active
        if (!this.isGroupedMode()) {
          seriesEntry.stack = 'stack1';
        }
        seriesData.push(seriesEntry);
      }
    });

    // Resolve style-config-driven options. Defaults are in pt so both the
    // string font-size fed to Highcharts and the px estimate used for the
    // margin budget below resolve from a single source — previously the
    // 14px / 8.5pt fallback split caused visible size jumps when styleConfig
    // was missing vs present.
    const sc = this.styleConfig ?? null;
    const tickFontPt = sc?.chartTickLabelSize ?? 11;
    const axisTitleFontPt = sc?.chartAxisTitleSize ?? 12;
    const legendFontPt = sc?.chartLegendSize ?? 11;
    const tickFontSize = `${tickFontPt}pt`;
    const axisTitleFont = `${axisTitleFontPt}pt`;
    const legendFontSize = `${legendFontPt}pt`;
    const gridlineWidth = sc ? (sc.chartShowGridlines ? 1 : 0) : 1;
    const legendOnRight = sc?.chartLegendPosition === 'Right';
    const seriesPalette: string[] | null =
      this.paletteColors && this.paletteColors.length ? this.paletteColors : null;

    // Font stack mirrors the body-text resolution in style-config-css.util.ts
    // (`'<configured family>', Arial, sans-serif`), so chart axes / legend /
    // tooltip read in the same family as the surrounding report copy.
    const configuredFamily: string | undefined = sc?.fontFamily;
    const fontStack =
      !configuredFamily || configuredFamily === 'System default'
        ? `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif`
        : `'${configuredFamily}', "Helvetica Neue", Helvetica, Arial, sans-serif`;

    // ── Per-chart-type label rotation ─────────────────────────────────
    // Resolution order:
    //   1. explicit `labelConfigOverride.labelRotation` (caller wins)
    //   2. per-type default: bar-family = 'auto' (Highcharts measures and
    //      picks 0 / -30 / -45 based on fit), everything else = 0
    // Output: a Highcharts xAxis.labels partial + a worst-case |angle|
    // for the bottom-margin reservation below.
    const BAR_FAMILY = ['bar', 'column', 'stacked-bar', 'stacked-column', 'stacked-percent-column'];
    const AUTO_ANGLES = [-30, -45];
    const overrideRot = this.labelConfigOverride?.labelRotation;
    const effectiveRotation: number | 'auto' | number[] =
      overrideRot !== undefined && overrideRot !== null
        ? overrideRot
        : BAR_FAMILY.includes(this.chartType)
        ? 'auto'
        : 0;

    // Translate the strategy into Highcharts xAxis.labels options.
    let labelRotationOptions: { rotation?: number; autoRotation?: number[] };
    if (effectiveRotation === 'auto') {
      labelRotationOptions = { autoRotation: AUTO_ANGLES };
    } else if (Array.isArray(effectiveRotation)) {
      labelRotationOptions = { autoRotation: effectiveRotation };
    } else if (effectiveRotation === 0) {
      // Force horizontal — pass autoRotation:[0] so Highcharts can't auto-tilt.
      labelRotationOptions = { rotation: 0, autoRotation: [0] };
    } else {
      labelRotationOptions = { rotation: effectiveRotation, autoRotation: undefined as any };
    }

    // Worst-case |angle| Highcharts might end up using — used as the upper
    // bound when reserving bottom margin.
    const maxAbsAngle =
      typeof effectiveRotation === 'number'
        ? Math.abs(effectiveRotation)
        : (effectiveRotation === 'auto' ? AUTO_ANGLES : effectiveRotation).reduce(
            (m, a) => Math.max(m, Math.abs(a)),
            0
          );

    // ── Per-chart visual config (resolved with override) ──────────────
    // Inlined locally because libs/ui can't depend on libs/features.
    const cfgOverride = this.labelConfigOverride ?? {};
    const isStacked = this.stackingMode === 'normal' || this.stackingMode === 'percent';
    const isPercentStacking = this.stackingMode === 'percent';
    const cfg = {
      // Default to plain decimal — show raw values, don't extract exponent
      // into the axis title. Callers can opt into 'exponentInTitle' or
      // 'scientific' via labelConfigOverride.
      yAxisScaleNotation: cfgOverride.yAxisScaleNotation ?? 'decimal',
      yAxisHeadroom: cfgOverride.yAxisHeadroom ?? (isPercentStacking ? 0 : isStacked ? 0.05 : 0.1),
      maxBarWidthPx: cfgOverride.maxBarWidthPx ?? (isStacked ? 80 : 60),
    };

    // ── Bottom-margin budget ──────────────────────────────────────────
    // Highcharts packs both axis labels and a bottom legend into the
    // chart's marginBottom. We need to reserve enough for whichever
    // rotation Highcharts will actually pick — reserving worst-case
    // when labels render horizontal leaves a big dead gap (the
    // "compressed-looking single-bar chart" symptom).
    // Reuse the pt-resolved sizes from above so margin estimate and rendered
    // text agree. 1pt = 1.333px at 96 dpi.
    const tickFontPx = tickFontPt * 1.333;
    const legendFontPx = legendFontPt * 1.333;
    const longestLabelChars = indicatorsToShow.reduce(
      (max, c) => Math.max(max, String(c ?? '').length),
      0
    );
    // Rough proportional-font average; safe overestimate for sans-serif.
    const avgCharPx = tickFontPx * 0.55;
    const labelTextWidthPx = longestLabelChars * avgCharPx;

    // Estimate the angle Highcharts will pick for `autoRotation`: it tries
    // 0° first and only rotates if labels don't fit horizontally within
    // their per-category slot. Assume ~720px usable plot width as a
    // reasonable proxy when we don't know the chart's actual width yet.
    const assumedPlotWidthPx = 720;
    const slotPerCategoryPx = assumedPlotWidthPx / Math.max(1, indicatorsToShow.length);
    const labelFitsHorizontal = labelTextWidthPx + 12 <= slotPerCategoryPx;
    const expectedAngle = (() => {
      if (typeof effectiveRotation === 'number') return Math.abs(effectiveRotation);
      // 'auto' or array — pick 0 if labels fit, else fall back to first angle in list.
      if (labelFitsHorizontal) return 0;
      const angles = effectiveRotation === 'auto' ? AUTO_ANGLES : effectiveRotation;
      return Math.abs(angles[0] ?? 45);
    })();

    // Vertical footprint of a rotated label = textWidth · sin(angle) +
    // ascender for the leading char. For horizontal labels we just need
    // one line of text with a small padding.
    const angleRad = (expectedAngle * Math.PI) / 180;
    const labelHeightEstimate =
      expectedAngle === 0
        ? Math.ceil(tickFontPx + 14)
        : Math.ceil(labelTextWidthPx * Math.sin(angleRad) + tickFontPx + 12);

    const legendItemCount = seriesData.length;
    const legendIsBottom = !legendOnRight;
    // Conservative ~3 items per row — overestimates rows for long item
    // names so the reserved bottom margin is always enough for the legend
    // to render in full (no Highcharts pagination, no clipped second row).
    const legendRowsEstimate = legendIsBottom ? Math.max(1, Math.ceil(legendItemCount / 3)) : 0;
    const legendRowHeightPx = Math.max(24, Math.ceil(legendFontPx * 1.6));
    const legendHeightEstimate = legendIsBottom ? legendRowsEstimate * legendRowHeightPx + 16 : 0;

    // Fixed pixel gap between x-axis labels and legend. Matches legend.margin below.
    const labelLegendGap = legendIsBottom ? 16 : 0;
    const computedMarginBottom = labelHeightEstimate + labelLegendGap + legendHeightEstimate + 8;

    // ── Y-axis scale (extract exponent for tiny / huge values) ────────
    // Only visible stages drive the axis range — hidden ones must not keep the
    // axis stretched after they're toggled off.
    const visibleSeries = seriesData.filter(s => s.visible !== false);
    const allNumericValues: number[] = [];
    for (const s of visibleSeries) {
      if (Array.isArray(s.data)) {
        for (const v of s.data) {
          if (typeof v === 'number' && isFinite(v)) allNumericValues.push(v);
        }
      }
    }

    // For stacked (non-percent) charts the visible bar height is the *sum* of
    // the segments at each category, not any single segment. Driving the axis
    // range off per-segment values would set yAxisMax to the tallest single
    // segment and clip the top of the stacked bar — so compute per-category
    // stack totals (positive and negative stacks separately) instead.
    const isStackedNormal = !this.isGroupedMode() && !isPercentStacking;
    let dataMin: number;
    let dataMax: number;
    if (isStackedNormal && allNumericValues.length > 0) {
      let posMax = 0;
      let negMin = 0;
      const catCount = indicatorsToShow.length;
      for (let i = 0; i < catCount; i++) {
        let pos = 0;
        let neg = 0;
        for (const s of visibleSeries) {
          const v = Array.isArray(s.data) ? s.data[i] : undefined;
          if (typeof v === 'number' && isFinite(v)) {
            if (v >= 0) pos += v;
            else neg += v;
          }
        }
        posMax = Math.max(posMax, pos);
        negMin = Math.min(negMin, neg);
      }
      dataMax = posMax;
      dataMin = negMin;
    } else {
      dataMin = allNumericValues.length ? Math.min(...allNumericValues) : 0;
      dataMax = allNumericValues.length ? Math.max(...allNumericValues) : 0;
    }
    const maxAbsValue = Math.max(Math.abs(dataMin), Math.abs(dataMax));

    // Pick the notation strategy (auto resolves now that we know the data).
    let resolvedNotation: 'decimal' | 'scientific' | 'exponentInTitle' = 'decimal';
    if (cfg.yAxisScaleNotation === 'auto') {
      resolvedNotation =
        maxAbsValue > 0 && (maxAbsValue < 1e-3 || maxAbsValue >= 1e4)
          ? 'exponentInTitle'
          : 'decimal';
    } else if (
      cfg.yAxisScaleNotation === 'exponentInTitle' ||
      cfg.yAxisScaleNotation === 'scientific'
    ) {
      // Honor 'exponentInTitle' / 'scientific' only when values warrant it; otherwise plain decimal.
      const needsScale = maxAbsValue > 0 && (maxAbsValue < 1e-3 || maxAbsValue >= 1e4);
      resolvedNotation = needsScale ? cfg.yAxisScaleNotation : 'decimal';
    }

    let axisScaleExponent = 0;
    let axisScaleSuperscript = '';
    if (resolvedNotation === 'exponentInTitle' && maxAbsValue > 0) {
      axisScaleExponent = Math.floor(Math.log10(maxAbsValue));
      const supMap: Record<string, string> = {
        '0': '⁰',
        '1': '¹',
        '2': '²',
        '3': '³',
        '4': '⁴',
        '5': '⁵',
        '6': '⁶',
        '7': '⁷',
        '8': '⁸',
        '9': '⁹',
        '-': '⁻',
      };
      axisScaleSuperscript = String(axisScaleExponent)
        .split('')
        .map(c => supMap[c] ?? c)
        .join('');
    }
    const axisScale = Math.pow(10, axisScaleExponent);
    const yAxisTitleText = axisScaleSuperscript
      ? `${this.indicatorsName} ×10${axisScaleSuperscript}`
      : this.indicatorsName;

    // ── Y-axis range with headroom + soft min/max ──────────────────────
    // Don't apply when stacked-percent (always 0-100) or no data.
    let yAxisMin: number | undefined;
    let yAxisMax: number | undefined;
    if (cfg.yAxisHeadroom > 0 && !isPercentStacking && allNumericValues.length > 0) {
      const range = dataMax - dataMin;
      if (range > 0) {
        const padding = range * cfg.yAxisHeadroom;
        // If all-positive data, keep floor at 0 (don't pad below 0).
        yAxisMin = dataMin >= 0 ? 0 : dataMin - padding;
        yAxisMax = dataMax + padding;
      }
    }

    // Don't let the plot area collapse — grow the chart height when
    // long labels eat all the vertical budget. Two scalers:
    //   1. floor: an absolute minimum so bars are never squat
    //   2. per-category bump: more categories need more vertical room so
    //      individual bars stay readable (especially in stacked-percent)
    const baseChartHeight = this.chartHeight || 500;
    const categoryCount = indicatorsToShow.length || 1;
    const targetPlotAreaPx = Math.max(380, Math.min(560, categoryCount * 36));
    const chartHeightPx = Math.min(
      1200,
      Math.max(baseChartHeight, computedMarginBottom + targetPlotAreaPx + 30)
    );
    this.computedChartHeight = chartHeightPx;

    // Bound formatter usable inside Highcharts callbacks (where `this` is the chart)
    const formatDisplayValue = (v: number) => this.formatTableValue(v);

    // Create chart options
    this.chartOptions = {
      chart: {
        type: this.chartType,
        height: chartHeightPx,
        marginBottom: computedMarginBottom,
        spacingBottom: 8,
        ...(legendOnRight ? { marginRight: 220 } : {}),
      },
      ...(seriesPalette ? { colors: seriesPalette } : {}),
      title: {
        text: '',
        style: {
          fontSize: axisTitleFont,
          fontFamily: fontStack,
        },
      },
      xAxis: {
        categories: indicatorsToShow,
        labels: {
          style: {
            fontSize: tickFontSize,
            fontFamily: fontStack,
          },
          padding: 0,
          ...labelRotationOptions,
        },
        gridLineWidth: gridlineWidth,
        gridLineDashStyle: 'Solid',
      },
      yAxis: {
        lineWidth: 1,
        tickWidth: 1,
        ...(yAxisMin !== undefined ? { min: yAxisMin, startOnTick: false } : {}),
        ...(yAxisMax !== undefined ? { max: yAxisMax, endOnTick: false } : {}),
        title: {
          text: yAxisTitleText,
          style: {
            fontSize: axisTitleFont,
            fontFamily: fontStack,
          },
        },
        ...(isPercentStacking
          ? {
              tickInterval: 10,
              plotLines: [
                {
                  value: 0,
                  width: 1.5,
                  color: '#666',
                  zIndex: 5,
                },
              ],
            }
          : {}),
        labels: {
          style: {
            fontSize: tickFontSize,
            fontFamily: fontStack,
          },
          formatter: function (this: any) {
            const v = Number(this.value);
            if (!isFinite(v)) return '';
            if (isPercentStacking) return v + '%';
            // exponentInTitle: ticks render as scaled integers / 1-decimal.
            if (resolvedNotation === 'exponentInTitle' && axisScale !== 1) {
              const scaled = v / axisScale;
              const isInt = Math.abs(scaled - Math.round(scaled)) < 1e-9;
              return isInt ? scaled.toFixed(0) : scaled.toFixed(1);
            }
            // scientific: render as `2.0 × 10⁻⁶`.
            if (resolvedNotation === 'scientific' && v !== 0) {
              const exp = Math.floor(Math.log10(Math.abs(v)));
              const mantissa = v / Math.pow(10, exp);
              const supMap: Record<string, string> = {
                '0': '⁰',
                '1': '¹',
                '2': '²',
                '3': '³',
                '4': '⁴',
                '5': '⁵',
                '6': '⁶',
                '7': '⁷',
                '8': '⁸',
                '9': '⁹',
                '-': '⁻',
              };
              const supExp = String(exp)
                .split('')
                .map(c => supMap[c] ?? c)
                .join('');
              return `${mantissa.toFixed(1)} × 10${supExp}`;
            }
            // Decimal: show the raw value. `toLocaleString()` without options
            // truncates at 3 fraction digits (so 5e-7 → "0"), which was the
            // "y-axis is showing zero" bug. Use the native String() conversion
            // — gives "0.000002" for 2e-6 and falls back to JS's compact "2e-9"
            // form only at extreme magnitudes that no useable decimal can show.
            if (v === 0) return '0';
            const abs = Math.abs(v);
            // Big numbers: group thousands but keep all significant digits.
            if (abs >= 1000) {
              return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
            }
            // Mid-range: native toString is clean ("0.000002", "1.5", "-2.5").
            return String(v);
          },
        },
        gridLineWidth: gridlineWidth,
        gridLineDashStyle: 'Solid',
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.value}</b><br/>',
        formatter: function (this: any): string | false {
          const value = this.y;
          if (value === null || value === undefined) return false;

          const pointIndex = typeof this.point.x === 'number' ? this.point.x : 0;
          const seriesName = this.series.name;

          let percentageValue = '0.00';
          const customData = (this.series.userOptions as any)?.custom;
          if (customData && Array.isArray(customData['percentages'])) {
            const percentages = customData['percentages'];
            if (percentages.length > pointIndex) {
              const impact = parseFloat(percentages[pointIndex]);
              if (impact > 0 && impact < 0.1) {
                percentageValue = '> 0';
              } else {
                percentageValue = impact.toFixed(2);
              }
            }
          }

          const displayValue = formatDisplayValue(Number(value));

          const color = legendColors[this.series.name] || this.color;
          const hasCustomPercentage =
            customData &&
            Array.isArray(customData['percentages']) &&
            customData['percentages'].length > 0;

          // For stacked-percent charts, Highcharts provides this.percentage automatically
          const hasHighchartsPercentage = typeof this.percentage === 'number';

          // For regular stacked bars (non-percent), compute pct against the
          // stack total so the tooltip reads "segment is X% of the bar".
          const stackTotal = this.point && (this.point as any).stackTotal;
          const hasStackTotal =
            !hasHighchartsPercentage && typeof stackTotal === 'number' && stackTotal !== 0;

          if (hasCustomPercentage) {
            return `<span style="color:${color}">\u25CF</span> ${seriesName}: <b>${displayValue}</b> (${percentageValue}%)`;
          }
          if (hasStackTotal) {
            const rawPct = (Number(value) / stackTotal) * 100;
            const pct = Math.abs(rawPct) < 0.1 && rawPct !== 0 ? '> 0' : rawPct.toFixed(2);
            const stackColor = legendColors[this.series.name] || this.color;
            return `<span style="color:${stackColor}">●</span> ${seriesName}: <b>${displayValue}</b> (${pct}%)`;
          }
          if (hasHighchartsPercentage) {
            const pct =
              this.percentage < 0.1 && this.percentage > 0 ? '> 0' : this.percentage.toFixed(2);
            return `<span style="color:${color}">\u25CF</span> ${seriesName}: <b>${displayValue}</b> (${pct}%)`;
          }
          return `<span style="color:${color}">\u25CF</span> ${seriesName}: <b>${displayValue}</b>`;
        },
      },
      plotOptions: this.isGroupedMode()
        ? {
            // Grouped bars — matches backend ISOReportBarchart.cshtml exactly
            column: {
              pointPadding: 0.2,
              borderWidth: 0,
              dataLabels: { enabled: false },
              maxPointWidth: cfg.maxBarWidthPx || undefined,
            },
          }
        : {
            // Stacked bars
            column: {
              stacking: this.resolveStacking() as any,
              borderRadius: 0,
              dataLabels: { enabled: false },
              maxPointWidth: cfg.maxBarWidthPx || undefined,
            },
            series: {
              stacking: this.resolveStacking() as any,
              states: { inactive: { enabled: false } },
              pointPlacement: 0,
            },
          },
      legend: {
        enabled: true,
        align: legendOnRight ? 'right' : 'center',
        verticalAlign: legendOnRight ? 'middle' : 'bottom',
        layout: legendOnRight ? 'vertical' : 'horizontal',
        // No maxHeight cap → no pagination buttons. The bottom margin is
        // sized to the full estimated legend height (see legendHeightEstimate
        // above) so the legend has room to render all rows in one go.
        maxHeight: undefined,
        // Disable Highcharts' "1/3 ▼" legend pagination outright — the
        // controls are unusable in PDF / static-image export contexts.
        navigation: { enabled: false },
        // Fixed pixel gap (independent of bar count) between x-axis and legend.
        margin: legendOnRight ? undefined : 16,
        padding: 8,
        useHTML: true,
        itemStyle: {
          fontSize: legendFontSize,
          fontFamily: fontStack,
          fontWeight: 'normal',
        },
        labelFormatter: function () {
          const color = legendColors[this.name] || this.color;
          return `<span style="display: flex; align-items: center;">
            <span style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
            <span style="font-size: ${legendFontSize}; font-weight: normal; font-family: ${fontStack};">${this.name}</span>
          </span>`;
        },
        symbolHeight: 0,
        symbolWidth: 0,
        symbolRadius: 0,
        itemMarginTop: 4,
        itemMarginBottom: 4,
        itemDistance: 20,
      },
      credits: {
        enabled: false,
      },
      // Defensive override so Highcharts' built-in PNG/PDF export module
      // (right-click → "Download …") inherits the no-pagination legend.
      // Our primary export path is Puppeteer rendering the live chart, so
      // this is a safety net rather than a hot-path code change.
      exporting: {
        chartOptions: {
          legend: {
            navigation: { enabled: false },
            maxHeight: undefined,
          },
        },
      },
      series: seriesData,
    };

    this.isLoading = true;
    this.cdr.detectChanges();
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    this.selectedRangeIndex = 0;
    this.viewMode = 'chart'; // Reset to chart view when toggling fullscreen

    if (this.isFullscreen) {
      // When entering fullscreen, show all indicators in chart and select all by default
      this.filteredIndicators = [...this.allIndicators];
      this.selectedIndicators = [...this.selectableIndicators];
      this.selectedLegendItems = [...this.selectableLegendItems];
    }

    this.initChart();
  }

  toggleAccordion(): void {
    this.isAccordionOpen = !this.isAccordionOpen;
  }

  onRangeChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedRangeIndex = parseInt(selectElement.value);
    // Re-initialize the chart with the new range filter applied
    this.initChart();
  }

  isIndicatorSelected(indicator: IndicatorOption): boolean {
    return this.selectedIndicators.some(i => i.index === indicator.index);
  }

  onCheckboxChange(indicator: IndicatorOption): void {
    const index = this.selectedIndicators.findIndex(i => i.index === indicator.index);

    if (index === -1) {
      this.selectedIndicators.push(indicator);
    } else {
      this.selectedIndicators.splice(index, 1);
    }

    this.onIndicatorSelectionChange();
  }
  toggleLegendAccordion(): void {
    this.isLegendAccordionOpen = !this.isLegendAccordionOpen;
  }

  isLegendItemSelected(legendItem: IndicatorOption): boolean {
    return this.isLegendSeriesVisible(legendItem.name);
  }

  onLegendCheckboxChange(legendItem: IndicatorOption): void {
    this.toggleLegendSeries(legendItem.name);
  }

  /**
   * Whether a stage (legend series) is currently shown. `selectedLegendItems`
   * is the single source of truth for visibility; before it is populated
   * (first render) every stage defaults to visible.
   */
  private isLegendSeriesVisible(name: string): boolean {
    if (!this.selectableLegendItems.length) return true;
    return this.selectedLegendItems.some(i => i.name === name);
  }

  /**
   * Show/hide a stage from the legend (bottom chart legend or sidebar
   * checkbox) and rebuild the chart so the y-axis rescales to the remaining
   * visible stages. Keeps at least one stage visible.
   */
  private toggleLegendSeries(name: string): void {
    const legendItem = this.selectableLegendItems.find(i => i.name === name);
    if (!legendItem) return;

    const index = this.selectedLegendItems.findIndex(i => i.name === name);
    if (index !== -1) {
      // Currently visible → hide, but never hide the last remaining stage.
      if (this.selectedLegendItems.length <= 1) return;
      this.selectedLegendItems.splice(index, 1);
    } else {
      this.selectedLegendItems.push(legendItem);
    }

    this.initChart();
    this.cdr.detectChanges();
  }
}
